// Notifications bell: green dot when this device hasn't seen every notification yet; opening the
// panel marks them all as seen.
(() => {
  const KEY = 'xo-notif-seen';
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; } };
  const ids = () => [...document.querySelectorAll('#xo-notifications [data-xo-notif-id]')].map((n) => n.dataset.xoNotifId);
  const dot = () => document.querySelector('[data-xo-notif-dot]');
  const refresh = () => {
    const seen = new Set(read());
    const unseen = ids().filter((id) => !seen.has(id));
    dot()?.toggleAttribute('hidden', unseen.length === 0);
    document.querySelectorAll('#xo-notifications [data-xo-notif-id]').forEach((n) => n.classList.toggle('is-new', !seen.has(n.dataset.xoNotifId)));
  };
  // Seen list is ADDED to, never replaced: signing out (fewer notifications on the page) no longer
  // forgets what was already seen, so signing back in shows only what is really new.
  const markAll = () => {
    try {
      const all = [...new Set([...read(), ...ids()])].slice(-400);
      localStorage.setItem(KEY, JSON.stringify(all));
    } catch (e) {}
    dot()?.setAttribute('hidden', '');
  };
  // Newest on top: orders carry the time of their latest change; store messages (no time in the
  // theme) use the moment this device first received them.
  const FIRST = 'xo-notif-first';
  const sortNewest = () => {
    const list = document.querySelector('#xo-notifications [data-xo-notif-list]');
    if (!list) return;
    let first = {};
    try { first = JSON.parse(localStorage.getItem(FIRST) || '{}'); } catch (e) {}
    const now = Math.floor(Date.now() / 1000);
    const items = [...list.querySelectorAll(':scope > [data-xo-notif-id]')];
    const time = (li) => {
      const t = parseInt(li.dataset.xoTime || '', 10);
      if (t) return t;
      const id = li.dataset.xoNotifId;
      if (!first[id]) first[id] = now;
      return first[id];
    };
    const keyed = items.map((li, i) => ({ li, i, t: time(li) }));
    keyed.sort((a, b) => b.t - a.t || a.i - b.i);
    keyed.forEach(({ li }) => list.append(li));
    try {
      const keep = Object.entries(first).sort((a, b) => b[1] - a[1]).slice(0, 200);
      localStorage.setItem(FIRST, JSON.stringify(Object.fromEntries(keep)));
    } catch (e) {}
  };
  // First time this customer signs in on this device (new phone, new browser): order updates older than
  // 2 days count as already read, so they don't all come back as unread. Recent ones still show as new.
  const bootCustomer = () => {
    const list = document.querySelector('#xo-notifications [data-xo-notif-list]');
    const id = list?.dataset.xoCustomer;
    if (!id) return;
    const flag = 'xo-notif-boot-' + id;
    try {
      if (localStorage.getItem(flag)) return;
      const cutoff = Math.floor(Date.now() / 1000) - 2 * 24 * 3600;
      const old = [...list.querySelectorAll('[data-xo-time]')].filter((li) => parseInt(li.dataset.xoTime, 10) < cutoff).map((li) => li.dataset.xoNotifId);
      localStorage.setItem(KEY, JSON.stringify([...new Set([...read(), ...old])].slice(-400)));
      localStorage.setItem(flag, '1');
    } catch (e) {}
  };
  const start = () => {
    bootCustomer();
    sortNewest();
    refresh();
    const dlg = document.getElementById('xo-notifications');
    if (!dlg) return;
    // mark as seen when the panel closes, so the "new" highlight is visible while it is open
    new MutationObserver(() => { if (!dlg.open) { markAll(); refresh(); } else dot()?.setAttribute('hidden', ''); })
      .observe(dlg, { attributes: true, attributeFilter: ['open'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();

// "Install the app" button (Android / desktop Chrome & Edge): shown only when the browser offers
// installation and the site isn't already running as the installed app.
(() => {
  let deferred = null;
  const btn = () => document.querySelector('[data-xo-install]');
  const standalone = () => window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e;
    if (!standalone()) btn()?.removeAttribute('hidden');
  });
  document.addEventListener('click', async (e) => {
    const b = e.target instanceof Element ? e.target.closest('[data-xo-install]') : null;
    if (!b || !deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    deferred = null;
    b.setAttribute('hidden', '');
  });
  window.addEventListener('appinstalled', () => btn()?.setAttribute('hidden', ''));
})();

// Safari (iPhone / iPad / Mac) has no install prompt: the same button opens a short how-to
// (Share → Add to Home Screen, or File → Add to Dock on Mac).
(() => {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const safari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS|Edg|OPR|Android/.test(ua);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  if (!safari || standalone) return;
  const mac = !iOS;
  const show = () => {
    const b = document.querySelector('[data-xo-install]');
    if (!b) return;
    b.removeAttribute('hidden');
    b.addEventListener('click', () => {
      document.getElementById('xo-install-help')?.remove();
      const box = document.createElement('div');
      box.id = 'xo-install-help';
      box.className = 'xo-install-help';
      box.setAttribute('role', 'dialog');
      const p = document.createElement('p');
      p.textContent = mac ? b.dataset.macText : b.dataset.iosText;
      const ok = document.createElement('button');
      ok.type = 'button';
      ok.className = 'xo-btn-primary';
      ok.textContent = b.dataset.okText || 'OK';
      ok.addEventListener('click', () => box.remove());
      box.append(p, ok);
      (b.closest('dialog') || document.body).append(box);
      ok.focus();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', show); else show();
})();

// Notifications follow the site language automatically. A message written in Arabic shows in English on the
// English site (and the other way round) using the browser's own built-in translator (free, on the device).
// Store messages translated in Translate & Adapt already arrive in the right language and are left as they are.
(() => {
  const AR = /[\u0600-\u06FF]/;
  let busy = false;
  const run = async () => {
    if (busy || !('Translator' in self)) return;
    const target = (document.documentElement.lang || 'ar').slice(0, 2);
    const nodes = [...document.querySelectorAll('#xo-notifications .xo-notif-item p')]
      .filter((p) => !p.dataset.xoTr && p.textContent.trim() && !p.querySelector('.xo-money, [data-xo-money]'));
    const jobs = nodes.map((p) => ({ p, src: AR.test(p.textContent) ? 'ar' : 'en' })).filter((j) => j.src !== target);
    if (!jobs.length) return;
    busy = true;
    try {
      const avail = await self.Translator.availability({ sourceLanguage: jobs[0].src, targetLanguage: target });
      if (avail === 'unavailable') return;
      const tr = await self.Translator.create({ sourceLanguage: jobs[0].src, targetLanguage: target });
      for (const j of jobs) {
        if (j.src !== jobs[0].src) continue;
        const parts = j.p.innerHTML.split(/<br\s*\/?>/i).map((s) => { const d = document.createElement('div'); d.innerHTML = s; return d.textContent; });
        const out = [];
        for (const s of parts) out.push(s.trim() ? await tr.translate(s) : s);
        j.p.textContent = '';
        out.forEach((s, i) => { if (i) j.p.append(document.createElement('br')); j.p.append(s); });
        j.p.dataset.xoTr = '1';
      }
    } catch (e) {} finally { busy = false; }
  };
  // Try quietly on load (works when the language pack is already on the device); otherwise on tapping the bell,
  // since the browser only downloads the pack after a tap.
  const start = () => { run(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[commandfor="xo-notifications"]')) run();
  }, true);
})();
